'use client'

import { useEffect, useRef, useState } from 'react'
import { UserPlus, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import DiditKycDialog from '@/components/didit/DiditKycDialog'

export function InviteFriendsButton({
  url,
  className,
  size = 18,
}: {
  url: string
  className?: string
  size?: number
}) {
  const [copied, setCopied] = useState(false)
  const [checking, setChecking] = useState(false)
  const [kycOpen, setKycOpen] = useState(false)
  const resetTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (resetTimeout.current) clearTimeout(resetTimeout.current)
  }, [])

  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      if (resetTimeout.current) clearTimeout(resetTimeout.current)
      resetTimeout.current = setTimeout(() => setCopied(false), 1600)
    } catch {
      toast.error('No se pudo copiar el enlace de invitación')
    }
  }

  async function handleCopy() {
    if (checking) return
    setChecking(true)
    try {
      // La API resuelve seller y customer desde la sesión; el cliente no decide la política.
      const response = await fetch('/api/kyc/didit/status?action=invite', {
        method: 'GET',
        cache: 'no-store',
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || data?.ok !== true) {
        throw new Error(data?.error || 'No se pudo validar el permiso para invitar')
      }
      if (data.allowed === true) {
        await copyInviteLink()
      } else {
        setKycOpen(true)
      }
    } catch (error) {
      // Fallar cerrado: un error de red nunca habilita compartir.
      toast.error(error instanceof Error ? error.message : 'No se pudo comprobar la verificación')
    } finally {
      setChecking(false)
    }
  }

  return (
    <>
      <Button
        type="button"
        onClick={() => { void handleCopy() }}
        disabled={checking}
        title={checking ? 'Comprobando identidad' : copied ? 'Link copiado' : 'Invitar amigos'}
        aria-label={checking ? 'Comprobando identidad' : copied ? 'Link copiado' : 'Invitar amigos'}
        className={cn(
          'hubee-header-control relative',
          '!h-12 !w-12 !rounded-[16px] !p-0',
          className
        )}
      >
        <span className="relative flex h-5 w-5 items-center justify-center">
          {checking ? (
            <Loader2 size={size} className="animate-spin" />
          ) : (
            <>
              <UserPlus
                size={size}
                className={cn(
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-foreground transition-all',
                  copied && 'scale-75 opacity-0'
                )}
              />
              <Check
                size={size}
                className={cn(
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-foreground transition-all',
                  copied ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                )}
              />
            </>
          )}
        </span>
      </Button>
      <DiditKycDialog
        open={kycOpen}
        onOpenChange={setKycOpen}
        action="invite"
        onApproved={async () => {
          // Vuelve a comprobar permisos en el servidor tras la verificación.
          const response = await fetch('/api/kyc/didit/status?action=invite', { cache: 'no-store' })
          const data = await response.json().catch(() => null)
          if (!response.ok || data?.allowed !== true) {
            throw new Error('La verificación todavía no permite compartir invitaciones')
          }
          await copyInviteLink()
        }}
      />
    </>
  )
}
